import { useEffect, useRef } from 'react';

/**
 * useReveal
 * Attaches an IntersectionObserver to the returned ref that adds an `is-visible`
 * class to every child element with the `reveal` class the first time it scrolls
 * into view. This centralises the reveal-on-scroll logic that was previously
 * duplicated in every section component.
 *
 * @param {Object} [options]
 * @param {number} [options.threshold=0.12]
 * @returns {React.MutableRefObject}
 */
const useReveal = ({ threshold = 0.12 } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    const nodes = root.querySelectorAll('.reveal');
    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, [threshold]);

  return ref;
};

export default useReveal;
